import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Home as HomeIcon,
  AttachMoney as SalaryIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

const API_BASE = process.env.REACT_APP_API_URL || "/api";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ru";

interface SalaryCalculation {
  id: number;
  master_id: number;
  master_name: string;
  master_phone: string;
  period_start: string;
  period_end: string;
  total_services: number;
  total_service_amount: number;
  total_adjustments: number;
  total_amount: number;
  status: "new" | "confirmed" | "paid";
  created_at: string;
  confirmed_at?: string;
  paid_at?: string;
}

interface ServiceEarning {
  id: number;
  service_name: string;
  client_name: string;
  service_price: number;
  compensation_type: string;
  compensation_value: number;
  earned_amount: number;
  service_date: string;
}

interface SalaryAdjustment {
  id: number;
  adjustment_type: "advance" | "bonus" | "penalty" | "compensation";
  amount: number;
  description: string;
  created_at: string;
}

const Salary: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [calculations, setCalculations] = useState<SalaryCalculation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [calculateDialogOpen, setCalculateDialogOpen] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Form states
  const [selectedMaster, setSelectedMaster] = useState<number | null>(null);
  const [periodStart, setPeriodStart] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [periodEnd, setPeriodEnd] = useState<Dayjs | null>(dayjs().endOf("month"));
  const [selectedCalculation, setSelectedCalculation] = useState<SalaryCalculation | null>(null);
  const [serviceEarnings, setServiceEarnings] = useState<ServiceEarning[]>([]);
  const [adjustments, setAdjustments] = useState<SalaryAdjustment[]>([]);
  
  // Adjustment form
  const [adjustmentType, setAdjustmentType] = useState<"advance" | "bonus" | "penalty" | "compensation">("bonus");
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentDescription, setAdjustmentDescription] = useState("");
  
  // Payment form
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");

  const [masters, setMasters] = useState<{ id: number; name: string; phone: string }[]>([]);

  useEffect(() => {
    fetchCalculations();
    fetchMasters();
  }, []);

  const fetchMasters = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/users/masters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setMasters(
          (Array.isArray(data) ? data : []).map((m: any) => ({
            id: m.id,
            name: m.full_name || m.name || m.phone,
            phone: m.phone,
          }))
        );
      }
    } catch (err) {
      console.error("Failed to fetch masters:", err);
    }
  };

  const fetchCalculations = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/salary/calculations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalculations(Array.isArray(data) ? data : data.calculations || []);
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка загрузки расчетов зарплаты");
      }
      
    } catch (err) {
      console.error("Failed to fetch calculations:", err);
      setError("Ошибка подключения к серверу");
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateSalary = async () => {
    if (!selectedMaster || !periodStart || !periodEnd) {
      setError("Выберите мастера и укажите период");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        master_id: String(selectedMaster),
        period_start: periodStart.startOf("day").toISOString(),
        period_end: periodEnd.endOf("day").toISOString(),
      });
      const response = await fetch(`${API_BASE}/salary/calculations/calculate?${params.toString()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setSuccess("Расчет зарплаты выполнен успешно");
        setCalculateDialogOpen(false);
        fetchCalculations(); // Обновляем список
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка расчета зарплаты");
      }
      
    } catch (err) {
      console.error("Failed to calculate salary:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleAddAdjustment = async () => {
    if (!selectedCalculation || !adjustmentAmount || !adjustmentDescription) {
      setError("Заполните все поля корректировки");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/salary/calculations/${selectedCalculation.id}/adjustments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          master_id: selectedCalculation.master_id,
          adjustment_type: adjustmentType,
          amount: parseFloat(adjustmentAmount),
          description: adjustmentDescription,
        }),
      });
      
      if (response.ok) {
        setSuccess("Корректировка успешно добавлена");
        setAdjustmentDialogOpen(false);
        setAdjustmentAmount("");
        setAdjustmentDescription("");
        // Перезагружаем детали открытого расчета и список
        if (selectedCalculation) {
          await handleViewDetails(selectedCalculation);
        }
        fetchCalculations();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка добавления корректировки");
      }
    } catch (err) {
      setError("Ошибка при добавлении корректировки");
    }
  };

  const handleUpdateStatus = async (calculationId: number, status: "confirmed" | "paid") => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE}/salary/calculations/${calculationId}/status?status=${encodeURIComponent(status)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        setSuccess(`Статус обновлен: ${status === "confirmed" ? "Подтверждено" : "Оплачено"}`);
        fetchCalculations(); // Обновляем список
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка обновления статуса");
      }
      
    } catch (err) {
      console.error("Failed to update status:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleViewDetails = async (calculation: SalaryCalculation) => {
    try {
      const token = localStorage.getItem("token");

      // Детали расчета содержат вложенные начисления и корректировки
      const calculationResponse = await fetch(`${API_BASE}/salary/calculations/${calculation.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (calculationResponse.ok) {
        const calcDetails = await calculationResponse.json();

        setSelectedCalculation({ ...calculation, ...calcDetails });
        setServiceEarnings(calcDetails.service_earnings || []);
        setAdjustments(calcDetails.adjustments || []);
        setDetailDialogOpen(true);
      } else {
        setError("Ошибка загрузки деталей расчета");
      }
      
    } catch (err) {
      console.error("Failed to fetch details:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "default";
      case "confirmed":
        return "primary";
      case "paid":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "new":
        return "Новый";
      case "confirmed":
        return "Подтвержден";
      case "paid":
        return "Выплачен";
      default:
        return status;
    }
  };

  const getAdjustmentTypeLabel = (type: string) => {
    switch (type) {
      case "advance":
        return "Аванс";
      case "bonus":
        return "Бонус";
      case "penalty":
        return "Штраф";
      case "compensation":
        return "Компенсация";
      default:
        return type;
    }
  };

  const getAdjustmentTypeColor = (type: string) => {
    switch (type) {
      case "advance":
        return "warning";
      case "bonus":
        return "success";
      case "penalty":
        return "error";
      case "compensation":
        return "info";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Box sx={{ width: "100%" }}>
          <Header title="Зарплата" />
          <LinearProgress />
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
      <Box sx={{ width: "100%", minHeight: "100vh", bgcolor: "background.default" }}>
        <Header title="Зарплата" />
        
        <Box sx={{ p: 3 }}>
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link color="inherit" href="#" onClick={() => navigate("/")}>
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Главная
            </Link>
            <Typography color="text.primary">
              <SalaryIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Зарплата
            </Typography>
          </Breadcrumbs>

          <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
            Зарплата и финансы мастеров
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Расчеты зарплаты" />
              <Tab label="Отчеты" />
              <Tab label="Настройки" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <>
              <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CalculateIcon />}
                  onClick={() => setCalculateDialogOpen(true)}
                >
                  Рассчитать зарплату
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Мастер</TableCell>
                      <TableCell>Период</TableCell>
                      <TableCell align="right">Услуг</TableCell>
                      <TableCell align="right">Начислено</TableCell>
                      <TableCell align="right">Корректировки</TableCell>
                      <TableCell align="right">Итого</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calculations.map((calc) => (
                      <TableRow key={calc.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {calc.master_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {calc.master_phone}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dayjs(calc.period_start).format("DD.MM.YYYY")} -{" "}
                            {dayjs(calc.period_end).format("DD.MM.YYYY")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{calc.total_services}</TableCell>
                        <TableCell align="right">
                          {calc.total_service_amount.toLocaleString()} ₽
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={calc.total_adjustments >= 0 ? "success.main" : "error.main"}
                          >
                            {calc.total_adjustments >= 0 ? "+" : ""}
                            {calc.total_adjustments.toLocaleString()} ₽
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {calc.total_amount.toLocaleString()} ₽
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(calc.status)}
                            color={getStatusColor(calc.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(calc)}
                            >
                              <EditIcon />
                            </IconButton>
                            {calc.status === "new" && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleUpdateStatus(calc.id, "confirmed")}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            )}
                            {calc.status === "confirmed" && (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleUpdateStatus(calc.id, "paid")}
                              >
                                <PaymentIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Отчеты будут доступны в следующей версии
              </Typography>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Настройки модуля зарплаты будут доступны в следующей версии
              </Typography>
            </Box>
          )}
        </Box>

        {/* Calculate Salary Dialog */}
        <Dialog
          open={calculateDialogOpen}
          onClose={() => setCalculateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Рассчитать зарплату</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Мастер</InputLabel>
                <Select
                  value={selectedMaster || ""}
                  label="Мастер"
                  onChange={(e) => setSelectedMaster(Number(e.target.value))}
                >
                  {masters.map((master) => (
                    <MenuItem key={master.id} value={master.id}>
                      {master.name} ({master.phone})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <DatePicker
                label="Начало периода"
                value={periodStart}
                onChange={(newValue: Dayjs | null) => setPeriodStart(newValue)}
                renderInput={(params) => <TextField {...params} sx={{ width: "100%" }} />}
              />
              
              <DatePicker
                label="Конец периода"
                value={periodEnd}
                onChange={(newValue: Dayjs | null) => setPeriodEnd(newValue)}
                renderInput={(params) => <TextField {...params} sx={{ width: "100%" }} />}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCalculateDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleCalculateSalary} variant="contained">
              Рассчитать
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog
          open={detailDialogOpen}
          onClose={() => setDetailDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Детали расчета - {selectedCalculation?.master_name}
          </DialogTitle>
          <DialogContent>
            {selectedCalculation && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Начисления за услуги
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Услуга</TableCell>
                            <TableCell>Клиент</TableCell>
                            <TableCell align="right">Стоимость</TableCell>
                            <TableCell align="right">Начислено</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {serviceEarnings.map((earning) => (
                            <TableRow key={earning.id}>
                              <TableCell>{earning.service_name}</TableCell>
                              <TableCell>{earning.client_name}</TableCell>
                              <TableCell align="right">
                                {earning.service_price.toLocaleString()} ₽
                              </TableCell>
                              <TableCell align="right">
                                {earning.earned_amount.toLocaleString()} ₽
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                      <Typography variant="h6">Корректировки</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAdjustmentDialogOpen(true)}
                      >
                        Добавить
                      </Button>
                    </Box>
                    
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Тип</TableCell>
                            <TableCell>Описание</TableCell>
                            <TableCell align="right">Сумма</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {adjustments.map((adjustment) => (
                            <TableRow key={adjustment.id}>
                              <TableCell>
                                <Chip
                                  label={getAdjustmentTypeLabel(adjustment.adjustment_type)}
                                  color={getAdjustmentTypeColor(adjustment.adjustment_type) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{adjustment.description}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  color={
                                    adjustment.adjustment_type === "advance" ||
                                    adjustment.adjustment_type === "penalty"
                                      ? "error.main"
                                      : "success.main"
                                  }
                                >
                                  {adjustment.adjustment_type === "advance" ||
                                  adjustment.adjustment_type === "penalty"
                                    ? "-"
                                    : "+"}
                                  {adjustment.amount.toLocaleString()} ₽
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Начислено за услуги
                    </Typography>
                    <Typography variant="h6">
                      {selectedCalculation.total_service_amount.toLocaleString()} ₽
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Корректировки
                    </Typography>
                    <Typography
                      variant="h6"
                      color={selectedCalculation.total_adjustments >= 0 ? "success.main" : "error.main"}
                    >
                      {selectedCalculation.total_adjustments >= 0 ? "+" : ""}
                      {selectedCalculation.total_adjustments.toLocaleString()} ₽
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Итого к выплате
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {selectedCalculation.total_amount.toLocaleString()} ₽
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Статус
                    </Typography>
                    <Chip
                      label={getStatusLabel(selectedCalculation.status)}
                      color={getStatusColor(selectedCalculation.status) as any}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>

        {/* Adjustment Dialog */}
        <Dialog
          open={adjustmentDialogOpen}
          onClose={() => setAdjustmentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Добавить корректировку</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Тип корректировки</InputLabel>
                <Select
                  value={adjustmentType}
                  label="Тип корректировки"
                  onChange={(e) => setAdjustmentType(e.target.value as any)}
                >
                  <MenuItem value="advance">Аванс (-)</MenuItem>
                  <MenuItem value="bonus">Бонус (+)</MenuItem>
                  <MenuItem value="penalty">Штраф (-)</MenuItem>
                  <MenuItem value="compensation">Компенсация (+)</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Сумма"
                type="number"
                value={adjustmentAmount}
                onChange={(e) => setAdjustmentAmount(e.target.value)}
                fullWidth
              />
              
              <TextField
                label="Описание"
                multiline
                rows={3}
                value={adjustmentDescription}
                onChange={(e) => setAdjustmentDescription(e.target.value)}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdjustmentDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleAddAdjustment} variant="contained">
              Добавить
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Messages */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
        
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default Salary;
